import { useMutation as useApolloMutation } from "@apollo/client";
import { toast } from "react-toastify";
import {
  CREATE_REWARD_SENDING_PLAN,
  CreateRewardSendingPlanRequestInput,
  UPDATE_REWARD_SENDING_PLAN,
  UpdateRewardSendingPlanRequestInput
} from "../../../graphql/operations";
import { useModulePlan } from "../../../hooks/useModulePlan";
import { EditMode, MembershipType, Plan } from "../../../models";
import { EmployerModuleType } from "../../../models/accounts/employerModuleType";
import { isLoading } from "../../../utils/isLoading";
import { useData } from "../../../utils/useData";
import { LoadingSpinner } from "../../shared/LoadingSpinner";
import { ModulePlanFormBase } from "../ModulePlanFormBase";
import { PlanFormValues } from "../PlanFormValues";
import { ModuleRenewalOptionsFields } from "../Standard/ModuleRenewalOptionsFields";
import { RewardReasonFields } from "./RewardReasonFields";
import { RewardSettingsFields } from "./RewardSettingsFields";
import { RewardsTiersFields } from "./RewardsTiersFields";

export const RewardsForm = ({
  plan,
  onClose
}: {
  plan?: Plan;
  onClose: () => void;
}) => {
  const { employerId, plans, serviceSectors, accounts, membership } = useData();

  const { initialValues, validationSchema } = useModulePlan(
    EmployerModuleType.REWARDS_RECOGNITION,
    plan
  );

  const editMode = plan == null ? EditMode.CREATE : EditMode.EDIT;
  const isPilot = membership.data?.type === MembershipType.PILOT;

  const [createRewardSendingPlan, { loading: createRewardLoading }] =
    useApolloMutation<boolean, CreateRewardSendingPlanRequestInput>(
      CREATE_REWARD_SENDING_PLAN,
      {
        onCompleted: () => {
          toast.success("Reward updated successfully");
          plans.refetch();
          onClose();
        },
        onError: (error) => {
          console.error(error.message);
        }
      }
    );

  const [updateRewardSendingPlan, { loading: updateRewardLoading }] =
    useApolloMutation<boolean, UpdateRewardSendingPlanRequestInput>(
      UPDATE_REWARD_SENDING_PLAN,
      {
        onCompleted: () => {
          toast.success("Reward updated successfully");
          plans.refetch();
          onClose();
        },
        onError: (error) => {
          console.error(error.message);
        }
      }
    );

  const handleRewardSave = async (values: PlanFormValues) => {
    if (!plan) {
      await createRewardSendingPlan({
        variables: {
          employerId: employerId,
          request: {
            name: values.name,
            type: values.type,
            tiers: values.tiers.map((tier) => {
              return {
                id: tier.id,
                name: tier.name,
                amount: tier.amount.amount
              };
            }),
            controls: values.controls,
            renewalOptions: values.renewalOptions
          }
        }
      });
    } else {
      await updateRewardSendingPlan({
        variables: {
          id: plan.id,
          employerId: employerId,
          request: {
            name: values.name,
            tiers: values.tiers.map((tier) => {
              return {
                id: tier.id,
                name: tier.name,
                amount: tier.amount.amount,
              };
            }),
            controls: values.controls
          }
        }
      });
    }
  };

  if (isLoading([serviceSectors])) return <LoadingSpinner />;

  return (
    <ModulePlanFormBase
      initialValues={initialValues}
      validationSchema={validationSchema}
      isLoading={createRewardLoading || updateRewardLoading}
      onClose={onClose}
      onSave={handleRewardSave}
    >
      <RewardSettingsFields />
      <ModuleRenewalOptionsFields isPilot={isPilot} editMode={editMode} />
      <RewardReasonFields />
      <RewardsTiersFields
        isPilot={isPilot}
        accounts={accounts.data!.items}
        editMode={editMode}
      />
    </ModulePlanFormBase>
  );
};
